import { Blueprint, BlueprintData } from '@rta/rta-blueprint-library';
import { Upload } from 'lucide-react';
import { useContext } from 'react';
import { useKeystoneSession } from '../../auth/use-keystone-session';
import { useNavigate } from 'react-router-dom';

import { AppContext, DesignActionTypes, sanitizeBlueprintData } from '@context';
import { Feature, FeatureHoookParams } from '..';
import { lang } from '@lang';
import { selectCustomerEmail, selectNavigation, useAppSelector } from '@state';
import { useAppToast } from '@hooks';

type UseImportBlueprintFeatureParams = FeatureHoookParams;

export const useImportBlueprintFeature = (_params: UseImportBlueprintFeatureParams = {}): Feature<null> => {
	const { user } = useKeystoneSession();
	const { appDispatch: dispatch } = useContext(AppContext);
	const appToast = useAppToast();
	const navigate = useNavigate();

	const customerEmail = useAppSelector(selectCustomerEmail);
	const nav = useAppSelector(selectNavigation);

	const targetEmail = customerEmail || nav.email || '';

	const [disabled, tooltip] = (() => {
		if (!targetEmail) return [true, lang.customer.tooltip.importBlueprintNoCustomer];
		return [false, lang.customer.tooltip.importBlueprint];
	})();

	const readFileAsText = (file: File): Promise<string> => new Promise((resolve, reject) => {
		const reader = new FileReader();
		reader.onload = () => resolve(reader.result as string);
		reader.onerror = () => reject(reader.error ?? new Error('Failed to read file'));
		reader.readAsText(file);
	});

	const importFromFile = async (file: File): Promise<void> => {
		try {
			const text = await readFileAsText(file);
			const parsed = JSON.parse(text) as BlueprintData;

			if (!parsed || typeof parsed !== 'object' || !parsed.kitchens) {
				throw new Error('Parsed payload is not a blueprint');
			}

			// re-key to current customer and force new identity so it loads as a fresh draft
			const rekeyed: BlueprintData = {
				...parsed,
				email: targetEmail,
				submissionId: null
			};
			// eslint-disable-next-line @typescript-eslint/no-explicit-any
			delete (rekeyed as any).id;

			const sanitized = sanitizeBlueprintData(rekeyed);
			const newBp = new Blueprint({ dataIn: sanitized, userEmail: user?.email });

			// make the imported blueprint editable: ensure a draft kitchen version exists
			const existingDraft = newBp.kitchens.findVersion(null);
			if (existingDraft === null) {
				const kv = newBp.kitchens.newVersionFromLatest();
				if (kv) kv.isTemp = true;
			}

			dispatch({
				type: DesignActionTypes.addBlueprint,
				payload: { blueprint: newBp, version: null, useDraft: true, isDirty: true }
			});

			navigate(`/${nav.appMode}/${targetEmail}/${newBp.id}?draft`);
			appToast.success({ title: lang.customer.message.importBlueprintSuccess });
		} catch (err) {
			console.error('[Import Blueprint] failed:', err);
			appToast.error({ title: lang.customer.message.importBlueprintFailed });
		}
	};

	const openFilePicker = (): void => {
		const input = document.createElement('input');
		input.type = 'file';
		input.accept = '.rbp,application/json';
		input.onchange = () => {
			const file = input.files?.[0];
			if (file) void importFromFile(file);
		};
		input.click();
	};

	return {
		name: 'importBlueprintFeature',
		label: lang.customer.label.importBlueprint,
		shortcut: '',
		tooltip,
		icon: (props = {}) => <Upload {...props} />,
		disabled,
		value: null,
		hidden: false,
		handler: () => {
			if (disabled) return;
			openFilePicker();
		}
	};
};
